import { useState, useRef, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { DateTime } from 'luxon'

import { setNotificationState } from '../redux/notificationsReducer'

import {
  Actions,
  CancelBtn,
  CloseBtn,
  InputBox,
  InputFieldModal,
  Modal,
  ModalContentWrapper,
  ModalContent,
  ModalHeader,
  ModalLabel,
  Select,
  StyledPopup,
  SubmitBtnModal,
} from './CommonStylesForms'

import { setAuthorInvitation, setCreateInvForm } from '../redux/schemasReducer'

function FormInvitation(props) {
  const dispatch = useDispatch()
  const schemasState = useSelector((state) => state.schemas)
  const notificationType = useSelector(
    (state) => state.notifications.notificationType
  )
  const settingsState = useSelector((state) => state.settings)

  const invitationForm = useRef()
  const submitBtn = useRef()

  const [multiUse, setMultiUse] = useState(false)
  const [outOfBand, setOutOfBand] = useState(false)

  useEffect(() => {
    if (notificationType === 'error' && submitBtn.current) {
      submitBtn.current.removeAttribute('disabled')
    }
  }, [notificationType])

  // Disable button on submit
  const onBtnClick = (e) => {
    if (submitBtn.current) {
      submitBtn.current.setAttribute('disabled', 'disabled')
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onBtnClick()

    const form = new FormData(invitationForm.current)
    let invitation = {}
    const oob = form.get('oob') === 'true' ? true : false
    const protocol = form.get('protocol')

    if (protocol === 'DID_EXCHANGE_1.0') {
      invitation.handshakeProtocol =
        'did:sov:BzCbsNYhMrjHiqZDTUASHg;spec/didexchange/1.0'
    } else if (protocol === 'DID_EXCHANGE_1.1') {
      invitation.handshakeProtocol = 'https://didcomm.org/didexchange/1.1'
    } else {
      invitation.handshakeProtocol =
        'did:sov:BzCbsNYhMrjHiqZDTUASHg;spec/connections/1.0'
    }

    const activeStartingAtTimestamp = form.get('invitationActiveStartingAt')
      ? DateTime.fromISO(form.get('invitationActiveStartingAt')).ts
      : undefined
    const activeEndingAtTimestamp = form.get('invitationActiveEndingAt')
      ? DateTime.fromISO(form.get('invitationActiveEndingAt')).ts
      : undefined

    invitation.alias = form.get('alias')
    invitation.invitationMode = form.get('invitationMode')
    invitation.accept = form.get('accept')
    // invitation.public = props.public !== undefined ? props.public : form.get('public') === 'true';
    invitation.public = form.get('public') === 'true' ? true : false
    invitation.invitationRole = form.get('invitationRole')
    invitation.invitationLabel = form.get('invitationLabel')
    invitation.invitationStatus = form.get('invitationStatus')
    invitation.invitationDescription = form.get('invitationDescription')
    invitation.purpose = form.get('purpose') || ''
    invitation.invitationActiveStartingAt = activeStartingAtTimestamp
    invitation.invitationActiveEndingAt = activeEndingAtTimestamp
    invitation.usesAllowed = form.get('usesAllowed')

    invitation.respondWithSuccess = true

    if (invitation.public && !oob) {
      //TODO: Support public CV1 invitations.
      // (eldersonar) I don't think we'll ever will... So maybe worth saying something like "Currently, only OOB supports public invitations."
      return dispatch(
        setNotificationState({
          message: 'Currently, only OOB supports public invitations.',
          type: 'error',
        })
      )
    }

    if (activeStartingAtTimestamp >= activeEndingAtTimestamp) {
      return dispatch(
        setNotificationState({
          message:
            'Active Starting Time cannot be after the Active Ending Time.',
          type: 'error',
        })
      )
    }

    if (oob) {
      props.sendRequest('OUT_OF_BAND', 'CREATE_INVITATION', invitation)
    } else {
      props.sendRequest('INVITATIONS', 'CREATE', invitation)
    }

    console.log('FORMINVITATION - Invitation is: ', invitation)
    if (props.onFormSubmit) {
      props.onFormSubmit()
    }
    dispatch(setCreateInvForm(true))
    closeCreateInvModal()
  }

  function closeCreateInvModal() {
    setOutOfBand(false)
    setMultiUse(false)
    props.closeCreateInvModal()
  }

  return (
    <StyledPopup
      open={props.createInvModalIsOpen}
      closeOnDocumentClick
      onClose={closeCreateInvModal}
    >
      <Modal>
        <ModalHeader>Create New Invitation</ModalHeader>
        <ModalContentWrapper>
          <ModalContent>
            <form id="form" onSubmit={handleSubmit} ref={invitationForm}>
              <InputBox>
                <ModalLabel htmlFor="oob">OOB Invitation</ModalLabel>
                <Select
                  onChange={(e) => setOutOfBand(e.target.value === 'true')}
                  name="oob"
                  id="oob"
                  required
                  defaultValue={
                    props.oob !== undefined ? props.oob.toString() : ''
                  }
                  disabled={props.oob === true}
                >
                  <option value=""></option>
                  <option value="true">True</option>
                  <option value="false">False</option>
                </Select>
              </InputBox>
              {outOfBand ? (
                <InputBox>
                  <ModalLabel htmlFor="protocol">Protocol</ModalLabel>
                  <Select name="protocol" id="protocol" required>
                    <option value={'DID_EXCHANGE_1.0'}>DID Exchange 1.0</option>
                    <option value={'DID_EXCHANGE_1.1'}>DID Exchange 1.1</option>
                    <option value={'CONNECTIONS_V1'}>Connections v.1</option>
                  </Select>
                </InputBox>
              ) : null}
              <InputBox>
                <ModalLabel htmlFor="alias">Alias</ModalLabel>
                <InputFieldModal
                  type="alias"
                  name="alias"
                  id="alias"
                  required
                />
              </InputBox>
              <InputBox>
                <ModalLabel htmlFor="Invitation Mode">
                  Invitation Mode
                </ModalLabel>
                <Select
                  onChange={(e) => setMultiUse(e.target.value === 'multi')}
                  name="invitationMode"
                  id="invitationMode"
                  required
                >
                  <option value=""></option> <option value="once">Once</option>
                  <option value="multi">Multi</option>
                </Select>
              </InputBox>
              {multiUse ? (
                <InputBox>
                  <ModalLabel htmlFor="Uses Allowed">Uses Allowed</ModalLabel>
                  <InputFieldModal
                    type="usesAllowed"
                    name="usesAllowed"
                    id="usesAllowed"
                  />
                </InputBox>
              ) : null}
              <InputBox>
                <ModalLabel htmlFor="Accept">Accept</ModalLabel>
                <Select name="accept" id="accept" required>
                  <option value=""></option>
                  <option value="auto">Auto</option>
                  <option value="manual">Manual</option>
                </Select>
              </InputBox>
              <InputBox>
                <ModalLabel htmlFor="public">Public</ModalLabel>
                <Select name="public" id="public" required>
                  <option value=""></option>
                  <option value="true">True</option>
                  <option value="false">False</option>
                </Select>
              </InputBox>
              <InputBox>
                <ModalLabel htmlFor="Role">Their Role</ModalLabel>
                <InputFieldModal
                  type="invitationRole"
                  name="invitationRole"
                  id="invitationRole"
                  value={props.their_role ? props.their_role : undefined}
                  readOnly={props.their_role ? true : undefined}
                />
              </InputBox>
              <InputBox>
                <ModalLabel htmlFor="Label">Their Label</ModalLabel>
                <InputFieldModal
                  // Use Organization name as default for nvitationLabel
                  defaultValue={settingsState.organizationName}
                  type="invitationLabel"
                  name="invitationLabel"
                  id="invitationLabel"
                />
              </InputBox>
              <InputBox>
                <ModalLabel htmlFor="Status">Workflow Status</ModalLabel>
                <Select name="invitationStatus" id="invitationStatus" required>
                  <option value=""></option> {' '}
                  <option value="active">Active</option> {' '}
                  <option value="inactive">Inactive</option>
                  <option value="deleted">Deleted</option>
                </Select>
              </InputBox>
              <InputBox>
                <ModalLabel htmlFor="Description">Description</ModalLabel>
                <InputFieldModal
                  type="invitationDescription"
                  name="invitationDescription"
                  id="invitationDescription"
                />
              </InputBox>
              <InputBox>
                <ModalLabel htmlFor="Purpose">Purpose</ModalLabel>
                <InputFieldModal
                  type="purpose"
                  name="purpose"
                  id="purpose"
                  placeholder="Unique purpose identifier (optional)"
                />
              </InputBox>
              <InputBox>
                <ModalLabel htmlFor="invitationActiveStartingAt">
                  Active Starting Time
                </ModalLabel>
                <InputFieldModal
                  type="datetime-local"
                  name="invitationActiveStartingAt"
                  id="invitationActiveStartingAt"
                />
              </InputBox>
              <InputBox>
                <ModalLabel htmlFor="invitationActiveEndingAt">
                  Active Ending Time
                </ModalLabel>
                <InputFieldModal
                  type="datetime-local"
                  name="invitationActiveEndingAt"
                  id="invitationActiveEndingAt"
                />
              </InputBox>
              <Actions>
                <CancelBtn
                  type="button"
                  onClick={closeCreateInvModal}
                  id="button-cancel"
                >
                  Cancel
                </CancelBtn>
                <SubmitBtnModal
                  type="submit"
                  ref={submitBtn}
                  id="button-submit"
                >
                  Submit
                </SubmitBtnModal>
              </Actions>
            </form>
          </ModalContent>
        </ModalContentWrapper>
        <CloseBtn onClick={closeCreateInvModal} id="button-exit">
          &times;
        </CloseBtn>
      </Modal>
    </StyledPopup>
  )
}

export default FormInvitation
